import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuth, useHasRole } from '@/lib/auth/AuthProvider';
import type { UserRole } from '@/types/auth';

interface ProtectedRouteProps {
  children: ReactNode;
  /** If set, user must have at least one of these roles. */
  roles?: UserRole[];
  /** Optional override for the redirect target on unauthenticated access. */
  redirectTo?: string;
}

/**
 * Gate a route behind authentication + optional role check.
 *
 * Three outcomes:
 *  - auth still bootstrapping     → render an empty placeholder
 *  - not authenticated            → trigger login (kc redirect) via /login
 *  - authenticated, role mismatch → render 403 stub
 */
export function ProtectedRoute({
  children,
  roles,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const { t } = useTranslation('auth');
  const { initialised, isAuthenticated } = useAuth();
  const hasRequiredRole = useHasRole(...(roles ?? []));

  if (!initialised) {
    return <div className="h-screen w-screen" aria-busy="true" />;
  }
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }
  if (roles && roles.length > 0 && !hasRequiredRole) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-body text-slate-600">{t('accessDenied.body')}</p>
      </div>
    );
  }
  return <>{children}</>;
}
